import { i18n } from '@/shared/i18n';
import type { ConnectorPlatform } from '@/entities/media-connector';
import type { ConnectorCapabilities, ConnectorHealth } from './types';
import {
  CREATIVE_FIELD_PREFIX,
  fieldDef,
  levelSchema,
  platformStructure,
  type PlatformLevel,
} from './platform-structure';

/**
 * A Job is one delivery of work: a source file, everything parsed out of it, and
 * the single publish that pushes the lot. It exists because "new campaign" is the
 * wrong unit — one spreadsheet routinely creates several campaigns, updates
 * others, and spans more than one platform, and all of that succeeds or fails as
 * one reviewed batch.
 */
export type JobStatus =
  | 'parsing'
  | 'needs_review'
  | 'ready'
  | 'publishing'
  | 'published'
  | 'partially_published'
  | 'failed';

// Getters, not values: a resolved label here would freeze English at import.
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  get parsing() {
    return i18n.t('projects:mediaCampaign.jobStatus.parsing');
  },
  get needs_review() {
    return i18n.t('projects:mediaCampaign.jobStatus.needsReview');
  },
  get ready() {
    return i18n.t('projects:mediaCampaign.jobStatus.readyToPublish');
  },
  get publishing() {
    return i18n.t('projects:mediaCampaign.jobStatus.publishing');
  },
  get published() {
    return i18n.t('projects:mediaCampaign.jobStatus.published');
  },
  get partially_published() {
    return i18n.t('projects:mediaCampaign.jobStatus.partlyPublished');
  },
  get failed() {
    return i18n.t('projects:mediaCampaign.jobStatus.failed');
  },
};

/**
 * `zip` is historical: a sheet and its creatives used to upload as one archive. The upload
 * route takes a sheet only now, so nothing new lands here as `zip`, but a job created before
 * that still reads as one.
 */
export type JobSourceKind = 'xlsx' | 'csv' | 'zip';

export interface JobSource {
  fileName: string;
  kind: JobSourceKind;
  /** Rows the parser read, excluding the header. Drives "42 rows read". */
  rowCount: number;
  uploadedAt: string;
  /** Whether the file itself is still held, and so can be handed back. */
  isDownloadable: boolean;
  /**
   * The template the parser recognised, or null when it fell back to mapping
   * columns by inference. Which one happened changes how much the reviewer
   * should trust the result, so it is worth showing.
   */
  templateName: string | null;
}

/** What the last server-side pre-flight found, and whether the plan has moved since. */
export interface JobPreflightSummary {
  lastRunAt: string;
  errors: number;
  warnings: number;
  advice: number;
  isStale: boolean;
}

/**
 * What the platform said when it was last asked, and how much of the plan it could
 * speak to.
 *
 * `unchecked` matters as much as the objections: an ad set or ad needs a live parent
 * to be validated against, so on a plan that creates everything most of it cannot be
 * checked, and "no objections" alone would read as full coverage.
 */
export interface JobDryRunSummary {
  lastRunAt: string;
  checked: number;
  rejected: number;
  unchecked: number;
}

export interface DeliveryJob {
  id: string;
  projectId: string;
  title: string;
  status: JobStatus;
  source: JobSource;
  /** Every platform the parsed plan touches, in the order they appear in the file. */
  platforms: ConnectorPlatform[];
  /**
   * Ads the plan holds now, as opposed to `source.rowCount`, which is what the file
   * said on upload and stops describing a plan anything is removed from.
   */
  adCount: number;
  createdAt: string;
  /** Last touched — an edit, a check, a publish. What a list is usually sorted by. */
  updatedAt: string;
  createdBy: string;
  publishedAt: string | null;
  /** Null until pre-flight has been run at least once. */
  preflight: JobPreflightSummary | null;
  /** Null until the platform has been asked at least once. */
  dryRun: JobDryRunSummary | null;
  /** Why the last zip read in the background did not become a plan. */
  sourceError: string | null;
  /** An uploaded zip is still being read into the plan. Only known on a single job. */
  isReadingSource: boolean;
}

/** What publishing this node will do on the platform. */
export type NodeAction = 'create' | 'update' | 'unchanged';

export const NODE_ACTION_LABELS: Record<NodeAction, string> = {
  get create() {
    return i18n.t('projects:mediaCampaign.nodeAction.new');
  },
  get update() {
    return i18n.t('projects:mediaCampaign.nodeAction.update');
  },
  get unchanged() {
    return i18n.t('projects:mediaCampaign.nodeAction.noChange');
  },
};

export interface JobFieldValue {
  key: string;
  value: string | null;
  /**
   * Set when the server reports this field as edited.
   *
   * The API reports only WHICH fields a reviewer changed, never what they held
   * before, and nothing stores the pre-edit value, so this flag is all there is.
   * Showing the original again means having the API retain it first.
   */
  edited?: boolean;
}

/**
 * `advice` is a guideline authored at the advise tier. Only `error` blocks
 * publishing; collapsing advice into error would gate a plan on a suggestion.
 */
export type JobIssueSeverity = 'error' | 'warning' | 'advice';

/**
 * Where a finding came from. A guideline is a house standard, not a platform rule.
 *
 * Mirrors media-api's own union exactly. The wire value is cast straight in, so a member
 * that exists only on this side would be one the server could set by accident.
 */
export type JobIssueSource = 'parse' | 'rule' | 'guideline' | 'platform' | 'advisory';

export interface JobIssue {
  severity: JobIssueSeverity;
  /** Canonical field key when the issue is about one field, else null. */
  field: string | null;
  message: string;
  source?: JobIssueSource;
  /**
   * Set only for the finding this app builds out of a row's stored publish failure.
   *
   * It is the platform's voice like any other `platform` finding and reads the same on the
   * card, but it is a record of something that already happened rather than an objection to
   * the plan as it stands — and the remedy for it is publishing again, so it must never be
   * what stops a publish.
   *
   * Deliberately not a `source` of its own. The wire value for `source` is cast straight in
   * from a `z.string()`, so a server that one day emitted the same word would silently take
   * a real rejection out of the publish gate. Nothing on the wire can set this field,
   * because {@link toJobIssue} builds every other one explicitly.
   */
  fromLastPublish?: boolean;
  /** Which rule produced it, where the UI offers an action for a particular one. */
  ruleId?: string;
  /**
   * A guideline finding carries the standard's own words. A platform finding has
   * none of this: the platform states a rule and offers no remedy, which is the
   * difference the drawer is built to show.
   */
  guidelineId?: string;
  why?: string;
  howToFix?: string;
  referenceUrl?: string;
  ownerName?: string;
}

export interface JobNode {
  id: string;
  level: PlatformLevel;
  action: NodeAction;
  /** Set when the node matched something already on the platform. */
  externalId: string | null;
  /** 1-based rows in the source file that produced this node. */
  sourceRows: number[];
  fields: JobFieldValue[];
  /** Issues the parser raised that validation cannot re-derive, e.g. an unmapped column. */
  parseIssues: JobIssue[];
  /**
   * How the last publish went for this row, and what the platform said when it refused.
   *
   * Persisted per node, unlike the publish report, which is the response to one call and
   * gone on reload. Without these a failed row is indistinguishable from one that was
   * never sent, which is the state someone returning to a partly published job is in.
   */
  publishOutcome?: NodePublishOutcome;
  publishError?: string | null;
  /** What the platform holds for this row, as of its publish or the last check. */
  deliveryState?: JobDeliveryState;
  /**
   * The creative this ad references, when the platform has one. Its values are shown
   * on this row under a prefix, and edits to them are addressed to this id.
   */
  creativeNodeId?: string;
  children: JobNode[];
}

export interface JobPlatformGroup {
  platform: ConnectorPlatform;
  /** The connected ad account this group publishes into. Null blocks publishing. */
  connectorId: string | null;
  accountName: string | null;
  /**
   * Whether that account can actually be reached, carried on the plan so the screen
   * can say so on open rather than after a check fails. Null when none is bound.
   */
  connectorStatus: ConnectorHealth | null;
  /**
   * What that account can currently do, per capability. Null when none is bound, and
   * optional so a plan fetched before capabilities existed still maps.
   */
  connectorCapabilities?: ConnectorCapabilities | null;
  /**
   * Whether the platform can be asked about the plan before anything is written. Only an
   * explicit false is said out loud: a plan fetched before the field existed says nothing,
   * because warning that a working connection cannot be checked is the worse mistake.
   */
  dryRunSupported?: boolean;
  /** The page this group's ads publish as. Null lets the account's own page stand. */
  pageId: string | null;
  pageName: string | null;
  /**
   * The client account this group buys for under a manager account (a Google Ads MCC): the
   * job's own choice, else the account's. Optional, like the capabilities above, so a plan
   * fetched before the field existed still maps.
   */
  advertiserId?: string | null;
  advertiserName?: string | null;
  /** True while the bound account reaches clients and none is chosen, which blocks publishing. */
  needsAdvertiser?: boolean;
  nodes: JobNode[];
}

export interface JobPlan {
  jobId: string;
  groups: JobPlatformGroup[];
}

export function nodeFieldValue(node: JobNode, key: string): string | null {
  return node.fields.find((field) => field.key === key)?.value ?? null;
}

export function nodeName(node: JobNode): string {
  // eslint-disable-next-line i18next/no-literal-string -- 'name' is the parsed field's key, not copy
  return nodeFieldValue(node, 'name') ?? i18n.t('projects:mediaCampaign.untitled');
}

export function isFieldEdited(field: JobFieldValue): boolean {
  return field.edited ?? false;
}

/**
 * Whether this row went out from this job.
 *
 * Neither signal alone. The id alone is wrong: a trafficker appending to a campaign that
 * already exists types its id into the sheet, and reading that as published locked the row the
 * moment it was uploaded, required-field errors included. The outcome alone is wrong too: a
 * failed or skipped row has one but no object behind it, and locking it left a partial publish
 * with no way to fix the row that failed and send it again.
 *
 * The same test as media-api's `publishedFromThisJob`, which refuses edits to exactly these rows.
 */
export function isNodePublished(node: Pick<JobNode, 'externalId' | 'publishOutcome'>): boolean {
  return (
    node.externalId !== null &&
    node.publishOutcome !== undefined &&
    node.publishOutcome !== 'pending'
  );
}

/**
 * A row that is live is read-only throughout.
 *
 * What has gone out is controlled where it is running, not from the plan that sent it —
 * managing published objects is a separate screen. This was briefly per field, allowing
 * anything the platform did not freeze at creation; the people who run these campaigns
 * asked for the simpler rule.
 *
 * Per row rather than per job, so a partially published plan keeps its failed rows
 * editable: those are exactly the ones worth fixing and sending.
 *
 * Fields the platform freezes at creation are still shown on an update — hiding them
 * would lose what the file carried — but cannot be sent, so they are read-only there.
 */
export function isNodeFieldEditable(
  platform: string,
  node: Pick<JobNode, 'level' | 'action' | 'externalId' | 'publishOutcome'>,
  key: string,
): boolean {
  if (isNodePublished(node)) return false;
  if (node.action !== 'update') return true;
  return fieldDef(platform, node.level, key)?.creationOnly !== true;
}

/** Set when pre-flight found the account already carrying this row's name. */
export const NAME_ALREADY_ON_PLATFORM = 'name-already-on-platform';

export function clashesWithExisting(node: Pick<JobNode, 'parseIssues' | 'externalId'>): boolean {
  // A row already bound to an object cannot also collide with one: it is that object.
  if (node.externalId !== null) return false;
  return node.parseIssues.some((issue) => issue.ruleId === NAME_ALREADY_ON_PLATFORM);
}

/**
 * Everything wrong with one node, from the platform's declared schema plus
 * whatever the parser already flagged.
 *
 * Required-field checks run against the platform's own list, so the same blank
 * cell is an error on Meta and fine on Google. That is the whole point of
 * reviewing per platform rather than against one house schema.
 */
export function nodeIssues(platform: string, node: JobNode): JobIssue[] {
  if (node.action === 'unchanged') return [...node.parseIssues];

  const derived: JobIssue[] = [];

  for (const def of levelSchema(platform, node.level).fields) {
    if (!def.required) continue;
    if (node.action === 'update' && def.creationOnly) continue;
    if (def.waivedBy && nodeFieldValue(node, def.waivedBy)?.trim()) continue;
    // The creative's columns ride on the ad row, but an ad the server built no creative for
    // has none to fill: a Google Search ad is its own copy, and an ad naming an asset already
    // in the account points at that. Whether a missing creative is wrong is the server's
    // call, and its rules say so as findings of their own.
    if (def.key.startsWith(CREATIVE_FIELD_PREFIX) && !node.creativeNodeId) continue;

    const value = nodeFieldValue(node, def.key);
    if (value === null || value.trim() === '') {
      derived.push({
        severity: 'error',
        field: def.key,
        message: `${def.label} is required by this platform.`,
      });
    }
  }

  // The server's required-field findings went stale the moment a reviewer filled a cell
  // in, since it does not recompute on edit. Live derivation owns the question, so a
  // stored one is dropped whether the field is still blank (a duplicate) or not (stale).
  //
  // A guideline finding is never a duplicate, however much it shares a field: the
  // platform requiring a value and a house standard requiring a *particular* value are
  // two different claims, and only the second carries a remedy worth reading.
  const derivedFields = new Set(derived.map((issue) => issue.field));
  const stored = node.parseIssues.filter(
    (issue) =>
      issue.source === 'guideline' ||
      (!issue.ruleId?.startsWith('required-fields-') &&
        !(issue.severity === 'error' && issue.field && derivedFields.has(issue.field))),
  );

  return [...stored, ...derived];
}

export interface JobNodeCounts {
  create: number;
  update: number;
  unchanged: number;
  /**
   * Errors that stand between this plan and a publish.
   *
   * Narrower than `errors`, which is everything the review shows as one. A failed publish
   * is shown as an error because a row that did not go out is a problem, but re-running
   * the publish is how it gets fixed; counting it here would mean a partly published job
   * could never be finished.
   */
  blockingErrors: number;
  errors: number;
  warnings: number;
  edited: number;
}

const EMPTY_COUNTS: JobNodeCounts = {
  create: 0,
  update: 0,
  unchanged: 0,
  blockingErrors: 0,
  errors: 0,
  warnings: 0,
  edited: 0,
};

function addCounts(a: JobNodeCounts, b: JobNodeCounts): JobNodeCounts {
  return {
    create: a.create + b.create,
    update: a.update + b.update,
    unchanged: a.unchanged + b.unchanged,
    blockingErrors: a.blockingErrors + b.blockingErrors,
    errors: a.errors + b.errors,
    warnings: a.warnings + b.warnings,
    edited: a.edited + b.edited,
  };
}

export function countNode(platform: string, node: JobNode): JobNodeCounts {
  const issues = nodeIssues(platform, node);
  const own: JobNodeCounts = {
    create: node.action === 'create' ? 1 : 0,
    update: node.action === 'update' ? 1 : 0,
    unchanged: node.action === 'unchanged' ? 1 : 0,
    blockingErrors: issues.filter((issue) => issue.severity === 'error' && !issue.fromLastPublish)
      .length,
    errors: issues.filter((issue) => issue.severity === 'error').length,
    warnings: issues.filter((issue) => issue.severity === 'warning').length,
    edited: node.fields.filter(isFieldEdited).length > 0 ? 1 : 0,
  };
  return node.children.reduce((acc, child) => addCounts(acc, countNode(platform, child)), own);
}

export function countGroup(group: JobPlatformGroup): JobNodeCounts {
  return group.nodes.reduce(
    (acc, node) => addCounts(acc, countNode(group.platform, node)),
    EMPTY_COUNTS,
  );
}

export function countPlan(plan: JobPlan): JobNodeCounts {
  return plan.groups.reduce((acc, group) => addCounts(acc, countGroup(group)), EMPTY_COUNTS);
}

/**
 * Why the Job cannot be published yet, or null when it can.
 *
 * Structured rather than a sentence because only the caller can name a platform:
 * the labels live in the connector entity, which this one may import types from
 * but not values.
 */
export type PlanBlocker =
  | { kind: 'unbound_accounts'; platforms: ConnectorPlatform[] }
  | { kind: 'clients_needed'; platforms: ConnectorPlatform[] }
  | { kind: 'field_errors'; count: number; platforms: ConnectorPlatform[] }
  | { kind: 'no_changes' };

/**
 * Unbound accounts come first: an operator who fixed every field only to be told
 * about a missing connector has wasted the trip. Both cases carry the platforms
 * at fault, because the review shows one platform at a time and a blocker that
 * does not say where to look is a hunt.
 */
export function planBlocker(plan: JobPlan): PlanBlocker | null {
  const unbound = plan.groups.filter((group) => group.connectorId === null);
  if (unbound.length > 0) {
    return { kind: 'unbound_accounts', platforms: unbound.map((group) => group.platform) };
  }

  // A manager account with no client publishes nothing either, and pre-flight cannot see it:
  // only publishing asks which client the job buys for.
  const clientless = plan.groups.filter((group) => group.needsAdvertiser);
  if (clientless.length > 0) {
    return { kind: 'clients_needed', platforms: clientless.map((group) => group.platform) };
  }

  const { blockingErrors } = countPlan(plan);
  if (blockingErrors > 0) {
    return {
      kind: 'field_errors',
      count: blockingErrors,
      platforms: plan.groups
        .filter((group) => countGroup(group).blockingErrors > 0)
        .map((group) => group.platform),
    };
  }

  const { create, update } = countPlan(plan);
  if (create + update === 0) return { kind: 'no_changes' };

  return null;
}

/**
 * Rows this platform's sheet shows: one per leaf object, which for every platform we
 * publish to is one per ad.
 *
 * Distinct from `countGroup`, which counts a node at every level the review shows. Both
 * are true and they differ — a plan of twelve ads has sixteen nodes once its campaign and
 * ad sets are counted — so a bare number has to be clear about which it is. Next to a
 * sheet numbered 1 to 12, "16" reads as a miscount. Neither is what a publish writes:
 * each ad's creative is folded into its row here and is an object of its own there.
 */
export function countRows(group: JobPlatformGroup): number {
  const leaf = leafLevelOf(group.platform);
  return flattenNodes(group.nodes).filter((node) => node.level === leaf).length;
}

/** The deepest level a platform has — the one the sheet lists a row per. */
export function leafLevelOf(platform: string): PlatformLevel {
  const order = platformStructure(platform).order;
  return order[order.length - 1];
}

/** Depth-first, parents before children, which is also publish order. */
export function flattenNodes(nodes: JobNode[]): JobNode[] {
  return nodes.flatMap((node) => [node, ...flattenNodes(node.children)]);
}

/**
 * `unchanged` and `skipped` are different answers: the first is a node the plan does
 * not change, the second is one that never got its turn because an ancestor failed.
 * Only the second is remaining work.
 */
export type JobPublishOutcome = 'created' | 'updated' | 'unchanged' | 'skipped' | 'failed';

/**
 * The same answers as stored against a node, plus `pending` for one no publish has
 * reached yet. The report has no such member because it describes a run that happened.
 */
export type NodePublishOutcome = JobPublishOutcome | 'pending';

/**
 * The report spans four levels where the review table has three: a creative is a
 * platform object of its own that has to exist before the ad referencing it, even
 * though its values ride on the ad's row.
 */
export type JobPublishLevel = PlatformLevel | 'creative';

/**
 * What the platform is doing with an object now, which is not what the publish said.
 *
 * A publish reports the write; review runs for hours afterwards and can refuse an ad
 * that was created cleanly. Absent until someone asks.
 */
export type JobDeliveryState =
  | 'active'
  | 'paused'
  | 'draft'
  | 'in_review'
  | 'rejected'
  | 'ended'
  | 'unknown';

export interface JobPublishNodeResult {
  nodeId: string;
  name: string;
  level: JobPublishLevel;
  platform: ConnectorPlatform;
  outcome: JobPublishOutcome;
  /** Why it was skipped, or the platform's own rejection. */
  reason?: string;
  deliveryState?: JobDeliveryState;
  /** The platform's own words for that state, including why it refused. */
  deliveryDetail?: string;
}

/** What a publish did, per node. */
export interface JobPublishReport {
  /** Where the run left the job: fully published, partially published, or failed. */
  status: JobStatus;
  results: JobPublishNodeResult[];
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
  failed: number;
  /** Zero until the platforms have been asked what they did with what we sent. */
  live: number;
  inReview: number;
  rejected: number;
  lastDeliveryCheckAt?: string;
}
